import React from "react";
import { useNavigate } from "react-router-dom";

function HomePage({ isDarkMode, toggleDarkMode, username, onUsernameChange }) {
  const navigate = useNavigate();

  const features = [
    {
      id: "problems",
      title: "Problem Selector",
      description: "Discover and filter Codeforces problems with advanced search capabilities",
      icon: "fas fa-search",
      color: "blue",
      path: "/problems",
      stats: ["37+ Tags", "Advanced Filtering", "Smart Search"],
      gradient: "from-blue-500 to-purple-600",
    },
    {
      id: "stats",
      title: "User Statistics",
      description: "Analyze your coding journey with detailed statistics and insights",
      icon: "fas fa-chart-bar",
      color: "green",
      path: "/stats",
      stats: ["Performance Analytics", "Problem Recommendations", "Progress Tracking"],
      gradient: "from-green-500 to-teal-600",
    },
    {
      id: "contest-tracker",
      title: "Contest Tracker",
      description: "Track solved problems across Div 1, Div 2, Div 3, Div 4, and Educational rounds",
      icon: "fas fa-trophy",
      color: "orange",
      path: "/contest-tracker",
      stats: ["5 Contest Sections", "Solved Problem Lists", "Per Contest Progress"],
      gradient: "from-orange-500 to-rose-600",
    },
    {
      id: "training",
      title: "Training",
      description: "Adaptive 30-minute drills that climb or drop by 100 rating based on your submits",
      icon: "fas fa-dumbbell",
      color: "cyan",
      path: "/training",
      stats: ["Tag or Any Focus", "Live Submission Sync", "Adaptive Ladder"],
      gradient: "from-cyan-500 to-emerald-600",
    },
    {
      id: "ranked",
      title: "Ranked",
      description: "Timed solo ladder with its own Elo — Sprint, Standard, and Marathon modes",
      icon: "fas fa-ranking-star",
      color: "violet",
      path: "/ranked",
      stats: ["Shared Elo Ladder", "3 Timed Modes", "Match History"],
      gradient: "from-violet-500 to-fuchsia-600",
    },
  ];

  return (
    <div
      className={`min-h-screen transition-all duration-500 ${
        isDarkMode ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" : "bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50"
      }`}
    >
      {/* Header */}
      <header className="relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className={`p-3 rounded-2xl ${isDarkMode ? "bg-gradient-to-br from-blue-500/20 to-purple-600/20" : "bg-gradient-to-br from-blue-100 to-purple-100"}`}>
                <i className={`fas fa-code text-2xl ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}></i>
              </div>
              <div>
                <h1 className={`text-3xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Codeforces Utils</h1>
                <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Your ultimate Codeforces companion</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleDarkMode}
                className={`p-3 rounded-2xl transition-all duration-300 hover:scale-110 ${
                  isDarkMode ? "bg-slate-800/50 hover:bg-slate-700/50 text-yellow-400" : "bg-slate-100 hover:bg-slate-200 text-slate-600"
                }`}
                title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
              >
                <i className={`fas fa-${isDarkMode ? "sun" : "moon"} text-lg`}></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <div className={`inline-flex items-center px-6 py-3 rounded-full mb-8 ${isDarkMode ? "bg-blue-500/10 border border-blue-500/20" : "bg-blue-50 border border-blue-200"}`}>
            <i className={`fas fa-rocket mr-2 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}></i>
            <span className={`font-medium ${isDarkMode ? "text-blue-300" : "text-blue-700"}`}>Enhanced Codeforces Experience</span>
          </div>
          <h2 className={`text-5xl md:text-6xl font-bold mb-6 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
            Master Competitive
            <span className="bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent"> Programming</span>
          </h2>
          <p className={`text-xl md:text-2xl mb-8 max-w-3xl mx-auto ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
            Discover problems, track progress, and analyze your journey with powerful tools designed for Codeforces enthusiasts.
          </p>
          {username && (
            <div
              className={`inline-flex items-center px-6 py-3 rounded-2xl mb-8 ${isDarkMode ? "bg-green-500/10 border border-green-500/20" : "bg-green-50 border border-green-200"}`}
            >
              <i className={`fas fa-user-check mr-3 ${isDarkMode ? "text-green-400" : "text-green-600"}`}></i>
              <span className={`font-medium ${isDarkMode ? "text-green-300" : "text-green-700"}`}>Welcome back, {username}!</span>
            </div>
          )}
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {features.map((feature) => (
            <div
              key={feature.id}
              className={`group relative rounded-3xl p-8 transition-all duration-500 hover:scale-105 cursor-pointer ${
                isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl hover:shadow-3xl" : "bg-white/80 border border-slate-200/50 shadow-xl hover:shadow-2xl"
              }`}
              onClick={() => navigate(feature.path)}
            >
              {/* Background Gradient */}
              <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>

              {/* Content */}
              <div className="relative">
                <div className="flex items-center space-x-4 mb-6">
                  <div className={`p-4 rounded-2xl bg-gradient-to-br ${feature.gradient}`}>
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>
                  <div>
                    <h3 className={`text-2xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{feature.title}</h3>
                    <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Click to explore</p>
                  </div>
                </div>

                <p className={`text-lg mb-6 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>{feature.description}</p>

                <div className="space-y-3 mb-6">
                  {feature.stats.map((stat, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className={`p-1 rounded-full bg-gradient-to-br ${feature.gradient}`}>
                        <i className="fas fa-check text-white text-xs"></i>
                      </div>
                      <span className={`font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>{stat}</span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  <button
                    className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 bg-gradient-to-r ${feature.gradient} text-white hover:scale-105 shadow-lg hover:shadow-xl`}
                  >
                    Get Started
                    <i className="fas fa-arrow-right ml-2"></i>
                  </button>
                  <div className={`p-2 rounded-xl ${isDarkMode ? "bg-slate-700/50" : "bg-slate-100"}`}>
                    <i className={`fas fa-arrow-right text-lg ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}></i>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Start Section */}
        {!username && (
          <div className={`rounded-3xl p-8 text-center ${isDarkMode ? "bg-slate-800/60 border border-slate-700/50" : "bg-white/80 border border-slate-200/50"}`}>
            <div className="mb-6">
              <div
                className={`inline-flex p-4 rounded-2xl mb-4 ${
                  isDarkMode ? "bg-gradient-to-br from-indigo-500/20 to-purple-600/20" : "bg-gradient-to-br from-indigo-100 to-purple-100"
                }`}
              >
                <i className={`fas fa-user-plus text-2xl ${isDarkMode ? "text-indigo-400" : "text-indigo-600"}`}></i>
              </div>
              <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Quick Start</h3>
              <p className={`text-lg ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>Enter your Codeforces username to unlock personalized features</p>
            </div>

            <div className="max-w-md mx-auto">
              <div className="flex space-x-3">
                <input
                  type="text"
                  placeholder="Your Codeforces username"
                  value={username}
                  onChange={(e) => onUsernameChange(e.target.value)}
                  className={`flex-1 px-4 py-3 rounded-xl border-2 transition-all duration-300 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 ${
                    isDarkMode ? "bg-slate-800/70 border-slate-600/50 text-slate-100 placeholder-slate-400" : "bg-white border-slate-200 text-slate-900 placeholder-slate-500"
                  }`}
                />
                <button
                  onClick={() => navigate("/stats")}
                  disabled={!username}
                  className="px-6 py-3 rounded-xl font-medium transition-all duration-300 bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  <i className="fas fa-arrow-right"></i>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-16">
          <p className={`text-sm ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Built with ❤️ for the competitive programming community</p>
        </div>
      </div>
    </div>
  );
}

export default HomePage;
